import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  TextField,
  Typography,
  Input,
  Select,
  MenuItem,
} from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CancelIcon from "@mui/icons-material/Cancel";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import { errorToast, successToast, warnToast } from "../../helpers";
import { UploadMedia } from "../../utils/uploadMedia";
import Loader from "../../helpers/constants/Loader";
import {
  useAddCategoryMutation,
  useLazyGetCategoryByIdQuery,
  useUpdateCategoryMutation,
} from "../../services/main";
import useTranslation from "../../hooks/Translation";

const AddCategoryMain = () => {
  const { _id } = useParams();
  const t = useTranslation();
  const navigate = useNavigate();
  const [profilePicture, setProfilePicture] = useState("");
  const [categoryData, setCategoryData] = useState<any>();
  const [addCategory, { isLoading }] = useAddCategoryMutation();
  const [getCategoryDetails] = useLazyGetCategoryByIdQuery();
  const [updateCategory] = useUpdateCategoryMutation();

  const [imageId, setImageId] = useState(0);

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    try {
      if (!file) {
        setProfilePicture("");
        // setImageError("Image is required");
        return;
      }

      const res = await UploadMedia(file);
      if (res?.status === 200) {
        const imageUrl = res?.data[0]?.media_url;
        setProfilePicture(imageUrl);
        setImageId(res?.data[0]?.id);
        successToast("Image uploaded successfully.");
      } else {
        errorToast(res?.message);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      errorToast("Error uploading image. Please try again.");
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title_en: categoryData?.category_name_en || "",
      title_ar: categoryData?.category_name_ar || "",
      title_russia: categoryData?.category_name_rs || "",

      // token: ""
    },
    validationSchema: Yup.object({
      title_en: Yup.string()
        .required(t.error.required)
        .max(80, t.error.maxValidation)
        .min(3, t.error.minValidation),
      title_ar: Yup.string()
        .required(t.error.required)
        .max(80, t.error.maxValidation)
        .min(3, t.error.minValidation),
      title_russia: Yup.string()
        .required(t.error.required)
        .max(80, t.error.maxValidation)
        .min(3, t.error.minValidation),
    }),
    onSubmit: async (values) => {
      if (!profilePicture) {
        warnToast(t.error.noImage);
        return;
      }

      const body = {
        category_image: imageId,
        category_name_en: values?.title_en,
        category_name_ar: values?.title_ar,
        category_name_rs: values?.title_russia,
      };
      if (!_id)
        try {
          const encryptedBody = generateEncryptedKeyBody(body) as any;
          const response = await addCategory(encryptedBody).unwrap();
          if (response.status === 200) {
            successToast(response.data.message || t.error.catAdd);
            navigate("/manage-categories/");
          } else {
            errorToast("error");
          }
        } catch (e: any) {
          errorToast(e?.data?.message);
        }
      else {
        try {
          const encryptedBody2 = generateEncryptedKeyBody(body) as any;
          const response = await updateCategory({
            id: _id,
            body: encryptedBody2,
          }).unwrap();
          if (response.status === 200) {
            successToast(t.error.catUpdate);
            navigate("/manage-categories/");
          } else {
            errorToast(response?.message);
          }
        } catch (e: any) {
          errorToast(e?.message);
        }
      }
    },
  });

  const getCategoryDetail = async (id: string) => {
    try {
      const res = await getCategoryDetails({ id: _id }).unwrap();
      if (res?.status === 200) {
        setCategoryData(res?.data);
        setProfilePicture(res?.data?.category_image?.media_url);
        setImageId(res?.data?.category_image?.id);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
    }
  };

  useEffect(() => {
    if (_id && _id !== "add") {
      getCategoryDetail(_id);
    }
  }, []);

  return (
    <>
      <div>
        {/* <Loader isLoad={isLoading} /> */}
        <div className="main_loyout">
          <div className="dashboard">
            <h1>{_id ? t.main.editCat : t.main.addCat}</h1>
            <Button
              className="btn btn_primary"
              onClick={() => {
                navigate("/manage-categories");
              }}
            >
              {t.main.back}
            </Button>
          </div>
          <Card className="cards">
            <form onSubmit={formik.handleSubmit}>
              <CardContent sx={{ p: 1 }}>
                <Grid container spacing={2}>
                  {/* Image Upload Section */}
                  <Grid item xs={12}>
                    <Typography className="custom_label">
                      {t.main.image}
                    </Typography>
                    {profilePicture ? (
                      <div className="upload_image_preview">
                        <CardMedia
                          component="img"
                          style={{ borderRadius: 0 }}
                          image={profilePicture}
                          alt="photo"
                        />
                        <CancelIcon
                          onClick={(e) => {
                            e.preventDefault();
                            setProfilePicture("");
                          }}
                        />
                      </div>
                    ) : (
                      <Box className="upload_image">
                        <label htmlFor="icon-button-file">
                          <Input
                            sx={{ display: "none" }}
                            id="icon-button-file"
                            type="file"
                            inputProps={{
                              accept: "image/png,image/jpeg",
                            }}
                            onChange={handleImageUpload}
                          />
                          <Button component="span" className="upload_image_btn">
                            <img
                              style={{ borderRadius: 0 }}
                              src="/static/images/product1.png"
                              alt=""
                            />
                            <CameraAltIcon />
                          </Button>
                        </label>
                      </Box>
                    )}
                  </Grid>

                  {/* Category Name Fields */}

                  <Grid item xs={4}>
                    <Typography className="custom_label">
                      {t.main.categoryEn}
                    </Typography>
                    <TextField
                      hiddenLabel
                      type="text"
                      name="title_en"
                      id="title_en"
                      inputProps={{ maxLength: 40 }}
                      variant="outlined"
                      fullWidth
                      value={formik.values.title_en}
                      placeholder={t.main.categoryEn}
                      className="text_field"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.title_en &&
                        Boolean(formik.errors.title_en)
                      }
                      helperText={
                        formik.touched.title_en && formik.errors.title_en
                          ? formik.errors.title_en.toString()
                          : ""
                      }
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <Typography className="custom_label">
                      {t.main.categoryAr}
                    </Typography>
                    <TextField
                      hiddenLabel
                      type="text"
                      name="title_ar"
                      id="title_ar"
                      inputProps={{ maxLength: 40 }}
                      variant="outlined"
                      fullWidth
                      value={formik.values.title_ar}
                      placeholder={t.main.categoryAr}
                      className="text_field"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.title_ar &&
                        Boolean(formik.errors.title_ar)
                      }
                      helperText={
                        formik.touched.title_ar && formik.errors.title_ar
                          ? formik.errors.title_ar.toString()
                          : ""
                      }
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <Typography className="custom_label">
                      {t.main.categoryRu}
                    </Typography>
                    <TextField
                      hiddenLabel
                      type="text"
                      name="title_russia"
                      id="title_russia"
                      inputProps={{ maxLength: 40 }}
                      variant="outlined"
                      fullWidth
                      value={formik.values.title_russia}
                      placeholder={t.main.categoryRu}
                      className="text_field"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.title_russia &&
                        Boolean(formik.errors.title_russia)
                      }
                      helperText={
                        formik.touched.title_russia &&
                        formik.errors.title_russia
                          ? formik.errors.title_russia.toString()
                          : ""
                      }
                    />
                  </Grid>
                </Grid>

                <div className="form_btn">
                  <Button
                    size="large"
                    type="submit"
                    className="btn btn_primary"
                  >
                    {t.main.save}
                  </Button>
                </div>
              </CardContent>
            </form>
          </Card>
        </div>
      </div>
    </>
  );
};

export default AddCategoryMain;
